import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Button, Modal, Label, TextInput, Progress } from 'flowbite-react'
import { useAuth } from '../../utils/Auth/AuthProvider'
import { GoogleGenAI, createPartFromUri } from '@google/genai'
import PizZip from 'pizzip'
import { DOMParser } from '@xmldom/xmldom'
import { questionConvertor } from '../../utils/questionConvertor'
import { extractQuestionsChunk, request } from '../../utils/remote'
import * as pdfjsLib from 'pdfjs-dist'
import { PDFDocument } from 'pdf-lib'
// @ts-ignore - url import for pdf.worker
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'

(pdfjsLib as any).GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

// simple chunker: join paragraphs and split into char chunks with overlap
const chunkText = (text: string, size = 3000, overlap = 400) => {
    const out: string[] = []
    let i = 0
    while (i < text.length) {
        const part = text.slice(i, i + size)
        out.push(part)
        if (i + size >= text.length) break
        i = i + size - overlap
    }
    return out
}

// Normalize and dedupe question objects coming from multiple chunk responses.
const normalizeStr = (s: any) => (s === null || s === undefined) ? '' : String(s).replace(/\s+/g, ' ').trim().toLowerCase()

const dedupeQuestions = (arr: any[]) => {
    const map = new Map<string, any>()
    for (const raw of arr || []) {
        const q = raw || {}
        const questionKey = normalizeStr(q.question)
        const a = normalizeStr(q.choice_a)
        const b = normalizeStr(q.choice_b)
        const c = normalizeStr(q.choice_c)
        const d = normalizeStr(q.choice_d)
        const key = `${questionKey}|${a}|${b}|${c}|${d}`

        if (!map.has(key)) {
            // make a normalized copy (preserve original casing for display but trim)
            map.set(key, {
                question: (q.question || '').toString().trim(),
                choice_a: (q.choice_a || '').toString().trim(),
                choice_b: (q.choice_b || '').toString().trim(),
                choice_c: (q.choice_c || '').toString().trim(),
                choice_d: (q.choice_d || '').toString().trim(),
                answer: (q.ans || '').toString().trim().toUpperCase(), // Changed 'ans' to 'answer'
                note: q.note || null,
                image: q.image || false
            })
        } else {
            // merge missing choices/answer/note/image into existing
            const existing = map.get(key)
            if (!existing.choice_a && q.choice_a) existing.choice_a = q.choice_a.toString().trim()
            if (!existing.choice_b && q.choice_b) existing.choice_b = q.choice_b.toString().trim()
            if (!existing.choice_c && q.choice_c) existing.choice_c = q.choice_c.toString().trim()
            if (!existing.choice_d && q.choice_d) existing.choice_d = q.choice_d.toString().trim()
            if ((!existing.answer || existing.answer === '') && q.ans) existing.answer = q.ans.toString().trim().toUpperCase() // Changed 'ans' to 'answer'
            if (!existing.note && q.note) existing.note = q.note
            if (!existing.image && q.image) existing.image = q.image
            map.set(key, existing)
        }
    }
    return Array.from(map.values())
}

interface ExtractProps {
    onExtract: (questions: any[]) => void;
    onRegisterExtractFn?: (fn: () => void) => void;
    isExtracting: boolean;
    setIsExtracting: React.Dispatch<React.SetStateAction<boolean>>;
}

const ExtractQuestionsFromFile = ({ onExtract, onRegisterExtractFn, isExtracting, setIsExtracting }: ExtractProps) => {
    const fileRef = useRef<File | undefined>(undefined)
    const { token } = useAuth()
    // sendByPage moved into modal flow; keep state only if needed later
    const [showChoiceModal, setShowChoiceModal] = useState(false)
    const [showPageOptions, setShowPageOptions] = useState(false)
    const [pageRange, setPageRange] = useState('')
    const [pagesPerFile, setPagesPerFile] = useState('5')
    const [progressMessage, setProgressMessage] = useState<string | null>(null)


    // Register the extract trigger with the parent component.
    // We register `triggerExtract` which will open a choice modal when
    // the parent asks to extract. This lets the user pick full-text vs pages.
    useEffect(() => {
        if (onRegisterExtractFn) {
            onRegisterExtractFn(triggerExtract);
        }
    }, [onRegisterExtractFn, pageRange, pagesPerFile]);

    const triggerExtract = () => {
        // parent requested extraction; open choice modal
        if (!fileRef.current) {
            alert('Please select a file first.')
            return
        }
        setShowChoiceModal(true)
    }

    const changeFileData = (e: React.ChangeEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement
        fileRef.current = (target.files as FileList)[0]
    }

    const [progress, setProgress] = useState<number | null>(null)
    // Gemini key prompt state
    const [showKeyModal, setShowKeyModal] = useState(false)
    const [geminiKeyInput, setGeminiKeyInput] = useState('')
    const [savingKey, setSavingKey] = useState(false)
    const keyPromiseRef = useRef<{ resolve: (val: string | null) => void } | null>(null)

    // Helper: fetch existing key or prompt user to enter one and save it.
    const getUserKeyOrPrompt = async (): Promise<string | null> => {
        // First try GET via centralized request helper
        try {
            const kd = await request('/api/user/gemini-key/', { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } })
            const userKey = kd?.has_key && kd?.api_key ? kd.api_key : null
            if (userKey) return userKey
        } catch (e) {
            console.warn('Could not fetch existing gemini key', e)
        }

        // Prompt user via modal and wait for input
        return await new Promise((resolve) => {
            keyPromiseRef.current = { resolve }
            setShowKeyModal(true)
        })
    }

    const saveGeminiKey = async (key: string) => {
        if (!token) return false
        setSavingKey(true)
        try {
            const resp = await request('/api/user/gemini-key/', { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ api_key: key }) })
            // `request` will throw on network/HTTP errors; assume success if no throw
            return !!resp
        } catch (e) {
            console.warn('Error saving API key', e)
            return false
        } finally {
            setSavingKey(false)
        }
    }

    const handleStartPageExtraction = () => {
        if (!fileRef.current) {
            alert('Please select a file first.')
            return
        }
        if (!pageRange || !pagesPerFile) {
            alert('Please provide both page range and pages per file.')
            return
        }
        setShowPageOptions(false)
        // start page-based extraction
        handleExtract(true)
    }

    const handleExtract = useCallback(async (usePages: boolean = false) => {
            const file = fileRef.current
            if (!file) {
                setIsExtracting(false); // Ensure loading state is reset if no file
                return;
            }
            if (usePages) {
                setIsExtracting(true)
                setProgress(0)
                setProgressMessage('Splitting PDF...')

                try {
                    const userKey = await getUserKeyOrPrompt()
                    if (!userKey) throw new Error('API key not available')

                    const ai = new GoogleGenAI({ apiKey: userKey.trim() })

                    const arrayBuffer = await file.arrayBuffer()
                    const pdfDoc = await PDFDocument.load(arrayBuffer)
                    const totalPages = pdfDoc.getPageCount()

                    const [start, end] = pageRange.split('-').map(Number)
                    const validatedStart = pageRange === 'full' ? 1 : Math.max(1, start)
                    const validatedEnd = pageRange === 'full' ? totalPages : Math.min(totalPages, end)
                    const chunkSize = Number(pagesPerFile)

                    const chunks: {start: number, end: number}[] = []
                    for (let i = validatedStart; i <= validatedEnd; i += chunkSize) {
                        chunks.push({
                            start: i,
                            end: Math.min(i + chunkSize - 1, validatedEnd)
                        })
                    }

                    const aggregated: any[] = []
                    let chunkIndex = 0

                    for (const chunk of chunks) {
                        const { start: subStart, end: subEnd } = chunk
                        const baseProgress = (chunkIndex / chunks.length) * 100
                        
                        setProgress(baseProgress)
                        setProgressMessage(`Processing pages ${subStart}-${subEnd}...`)

                        const subDoc = await PDFDocument.create()
                        const copiedPages = await subDoc.copyPages(pdfDoc, Array.from({ length: subEnd - subStart + 1 }, (_, k) => subStart + k - 1))
                        copiedPages.forEach(page => subDoc.addPage(page))

                        const pdfBytes = await subDoc.save()
                        const pdfBlob = new Blob([pdfBytes.slice().buffer], { type: 'application/pdf' })

                        // Upload
                        setProgress(baseProgress + (100 / chunks.length) * 0.2)
                        setProgressMessage(`Uploading pages ${subStart}-${subEnd} (${chunkIndex + 1}/${chunks.length})...`)
                        const uploadedFile = await ai.files.upload({
                            file: pdfBlob,
                            config: { displayName: `${file.name}_${subStart}-${subEnd}.pdf` }
                        })

                        // Wait for processing
                        let getFile = await ai.files.get({ name: uploadedFile.name as string })
                        while (getFile.state === 'PROCESSING') {
                            setProgress(baseProgress + (100 / chunks.length) * 0.5)
                            setProgressMessage(`Waiting for file processing: ${getFile.state} (${chunkIndex + 1}/${chunks.length})`)
                            await new Promise(resolve => setTimeout(resolve, 5000))
                            getFile = await ai.files.get({ name: uploadedFile.name as string })
                        }

                        if (getFile.state === 'FAILED') {
                            throw new Error(`File processing failed for pages ${subStart}-${subEnd}.`)
                        }

                        // Extract
                        setProgress(baseProgress + (100 / chunks.length) * 0.8)
                        setProgressMessage(`Extracting questions from pages ${subStart}-${subEnd} (${chunkIndex + 1}/${chunks.length})...`)
                        const prompt = "Please extract the questions from the document. For each question, provide the question, four choices (a, b, c, d), and the correct answer. if the choice in the document is more than 4, determine the correct answer first and keep 4 choices including the right answer. also provide 'note', if the question depends on any data like essay or constant values mentioned in the document and needed to do the question, the note should be that. otherwise leave it null. and 'image', if the question or choice contains any image, it should be true. if there is any formular in the question or choice, the formula should be in latex format. If there is no answer mentioned in the text, generate the right answer and the answer should be in label A/B/C/D chosing from the choices. Don't include the question number in question. Format the output as a JSON array of objects, where each object has keys 'question', 'choice_a', 'choice_b', 'choice_c', 'choice_d', 'ans', 'note' and 'image'."
                        const contents: any[] = [prompt]
                        if (uploadedFile.uri && uploadedFile.mimeType) {
                            contents.push(createPartFromUri(uploadedFile.uri, uploadedFile.mimeType))
                        }

                        const resp: any = await ai.models.generateContent({ model: 'gemini-2.5-flash', contents })
                        const out = resp?.text ?? ''
                        let parsedQuestions: any[] | null = null
                        try {
                            parsedQuestions = JSON.parse(out)
                            if (!Array.isArray(parsedQuestions)) parsedQuestions = null
                        } catch (e) {
                            const m = String(out).match(/(\[\s*\{[\s\S]*?\}\s*\])/)
                            if (m) {
                                try { parsedQuestions = JSON.parse(m[1]) } catch (ee) { parsedQuestions = null }
                            }
                        }
                        if (parsedQuestions && Array.isArray(parsedQuestions)) {
                            aggregated.push(...parsedQuestions)
                        }
                        chunkIndex++
                    }

                    setProgress(100)
                    setProgressMessage('Extraction Complete!')
                    onExtract(dedupeQuestions(aggregated))

                } catch (err) {
                    console.error('Page-by-page extraction failed:', err)
                    alert(`An error occurred during page-by-page extraction: ${err instanceof Error ? err.message : String(err)}`)
                    setProgressMessage(null)
                } finally {
                    setIsExtracting(false)
                    setTimeout(() => {
                        setProgress(null)
                        setProgressMessage(null)
                    }, 2000)
                }
                return
            }

        // setIsExtracting(true) // This is now set by the parent ExamAddWizard

            const name = (file.name || '').toLowerCase()
            let joined = ''
            let heuristicFallback: any[] | null = null

            try {
                if (name.endsWith('.pdf')) {
                    // Use pdf.js to extract text from PDF
                    const arrayBuffer = await file.arrayBuffer()
                    const loadingTask = (pdfjsLib as any).getDocument({ data: arrayBuffer })
                    const pdf = await loadingTask.promise
                    const total = pdf.numPages as number
                    const texts: string[] = []
                    for (let p = 1; p <= total; p++) {
                        const page = await pdf.getPage(p)
                        const content = await page.getTextContent()
                        const text = (content.items || []).map((it: any) => it.str).join(' ')
                        texts.push(`\n\n=== Page ${p} ===\n` + text)
                    }
                    try { await pdf.destroy?.() } catch (err) {}
                    joined = texts.join('\n')
                    // derive heuristic paragraphs by splitting on blank lines
                    const paragraphsArr = joined.split(/\n\s*\n/).map(s => s.trim()).map(s => s === '' ? ' ' : s)
                    heuristicFallback = questionConvertor(paragraphsArr)
                } else if (name.endsWith('.docx')) {
                        // Read as ArrayBuffer and parse raw paragraphs from docx
                        const arrayBuffer = await file.arrayBuffer()
                        const zip = new PizZip(arrayBuffer as any)
                        const xml = new DOMParser().parseFromString(zip.files['word/document.xml'].asText(), 'text/xml')
                        const paragraphsXml = xml.getElementsByTagName('w:p')
                        const paragraphsArr: string[] = []
                        for (let i = 0; i < paragraphsXml.length; i++) {
                            let fullText = ''
                            const textsXml = paragraphsXml[i].getElementsByTagName('w:t')
                            for (let j = 0; j < textsXml.length; j++) {
                                const textXml = textsXml[j]
                                if (textXml.childNodes) fullText += textXml.childNodes[0].nodeValue
                            }
                            if (fullText === '') paragraphsArr.push(' ')
                            else paragraphsArr.push(fullText)
                        }
                        // heuristicFallback uses questionConvertor to produce question objects
                        heuristicFallback = questionConvertor(paragraphsArr)
                        joined = paragraphsArr.join('\n\n')
                } else if (name.endsWith('.doc')) {
                    // .doc (binary) is not reliably parsed in-browser — ask user to convert or use server-side flow
                    alert('The .doc format is not supported in-browser. Please convert the file to .docx or PDF, or use the server-side upload option.')
                    return
                } else {
                    alert('Unsupported file type. Please select a PDF or DOCX document.')
                    return
                }
            } catch (fileErr) {
                console.error('Failed to extract text from file', fileErr)
                alert('Failed to read the selected file. Please ensure it is a valid PDF or DOCX and try again.')
                return
            }
            // First, try a single direct Gemini call over the entire joined text (shortened if huge).
            try {
                // mark extracting now that full-text direct extraction is about to begin
                setIsExtracting(true)
                const userKey = await getUserKeyOrPrompt()
                if (userKey) {
                    try {
                        const ai = new GoogleGenAI({ apiKey: userKey.trim() })
                        const prompt = "Please extract the questions from the document. For each question, provide the question, four choices (a, b, c, d), and the correct answer. if the choice in the document is more than 4, determine the correct answer first and keep 4 choices including the right answer. also provide 'note', if the question depends on any data like essay or or constant values mentioned in the document and needed to do the question, the note should be that. otherwise leave it null. and 'image', if the question or choice contains any image, it should be true. if there is any formular in the question or choice, the formula should be in latex format. If there is no answer mentioned in the text, generate the right answer for the question and the answer should be in label A/B/C/D chosing from the choices. Don't include the question number in question. Format the output as a JSON array of objects, where each object has keys 'question', 'choice_a', 'choice_b', 'choice_c', 'choice_d', 'ans', 'note' and 'image'."
                        const modelName = 'gemini-2.5-flash'
                        const contents = `${prompt}\n\n${joined.slice(0, 250000)}`
                        const resp: any = await ai.models.generateContent({ model: modelName, contents })
                        const out = resp?.text ?? ''
                        let parsedQuestions: any[] | null = null
                        try {
                            parsedQuestions = JSON.parse(out)
                            if (!Array.isArray(parsedQuestions)) parsedQuestions = null
                        } catch (e) {
                            const m = String(out).match(/(\[\s*\{[\s\S]*?\}\s*\])/)
                            if (m) {
                                try { parsedQuestions = JSON.parse(m[1]) } catch (ee) { parsedQuestions = null }
                            }
                        }
                        if (parsedQuestions && Array.isArray(parsedQuestions)) {
                            setProgress(100)
                            onExtract(dedupeQuestions(parsedQuestions))
                            setIsExtracting(false)
                            return
                        }
                    } catch (err) {
                        console.warn('Direct Gemini extraction failed, will fall back to chunked extraction', err)
                    }
                }
            } catch (err) {
                console.warn('Failed to call direct gemini key endpoint', err)
            }

            // chunk and fallback logic (per-chunk gemini or server proxy)
            // Start extracting state for chunked processing
            setIsExtracting(true)
            const chunks = chunkText(joined, 3000, 400)
            const aggregated: any[] = []
                try {
                    // Try to fetch saved API key from backend (only the current user can retrieve their key)
                    let userKey: string | null = null
                    try {
                        const k = await getUserKeyOrPrompt()
                        if (k) userKey = k
                    } catch (e) {
                        console.warn('Could not fetch user gemini key from server, will use proxy', e)
                    }

                    if (userKey) {
                    // Attempt direct frontend Gemini calls using the user's key via @google/genai client
                    const ai = new GoogleGenAI({ apiKey: userKey.trim() })
                    for (let idx = 0; idx < chunks.length; idx++) {
                        setProgress(Math.round(((idx) / chunks.length) * 100))
                        try {
                            const prompt = "Please extract the questions from the document. For each question, provide the question, four choices (a, b, c, d), and the correct answer. if the choice in the document is more than 4, determine the correct answer first and keep 4 choices including the right answer. also provide 'note', if the question depends on any data like essay or constant values mentioned in the document and needed to do the question, the note should be that (You should never generate a note). otherwise leave it null. and 'image', if the question or choice contains any image, it should be true. if there is any formular in the question or choice, the formula should be in latex format. If there is no answer mentioned in the text, generate the right answer and the answer should be in label A/B/C/D chosing from the choices. Don't include the question number in question. Format the output as a JSON array of objects, where each object has keys 'question', 'choice_a', 'choice_b', 'choice_c', 'choice_d', 'ans', 'note' and 'image'."
                            const modelName = 'gemini-2.5-flash' // use example model
                            const contents = `${prompt}\n\n${chunks[idx]}`
                            const resp: any = await ai.models.generateContent({ model: modelName, contents })
                            const out = resp?.text ?? ''
                            let parsedQuestions: any[] | null = null
                            try {
                                parsedQuestions = JSON.parse(out)
                                if (!Array.isArray(parsedQuestions)) parsedQuestions = null
                            } catch (e) {
                                const m = String(out).match(/(\[\s*\{[\s\S]*?\}\s*\])/)
                                if (m) {
                                    try { parsedQuestions = JSON.parse(m[1]) } catch (ee) { parsedQuestions = null }
                                }
                            }
                            if (parsedQuestions && Array.isArray(parsedQuestions)) {
                                aggregated.push(...parsedQuestions)
                            } else {
                                // fallback for this chunk to the server proxy
                                const proxyRes = await extractQuestionsChunk({ toBeValidated: token, text: chunks[idx] })
                                if (proxyRes && Array.isArray(proxyRes.questions)) aggregated.push(...proxyRes.questions)
                            }
                        } catch (gerr) {
                            console.warn('gemini client chunk failed, will retry via proxy', gerr)
                            const proxyRes = await extractQuestionsChunk({ toBeValidated: token, text: chunks[idx] })
                            if (proxyRes && Array.isArray(proxyRes.questions)) aggregated.push(...proxyRes.questions)
                        }
                    }
                    setProgress(100)
                    onExtract(dedupeQuestions(aggregated))
                } else {
                    // No user key available; use server-side proxy per chunk
                    for (let idx = 0; idx < chunks.length; idx++) {
                        setProgress(Math.round(((idx) / chunks.length) * 100))
                        const res = await extractQuestionsChunk({ toBeValidated: token, text: chunks[idx] })
                        if (res && Array.isArray(res.questions)) aggregated.push(...res.questions)
                    }
                    setProgress(100)
                    onExtract(dedupeQuestions(aggregated))
                }
            } catch (e) {
                console.error('chunk extraction failed', e)
                // fallback: pass heuristic parsed questions (if available)
                onExtract(dedupeQuestions(heuristicFallback || []))
            } finally {
                setIsExtracting(false); // Ensure loading state is reset
                setTimeout(() => setProgress(null), 800)
            }
        }, [token, onExtract, setIsExtracting, setProgress]) // Add dependencies for useCallback

    return (
        <div>
            <h1 className='pb-3 pt-3 font-bold'>Choose Document (.pdf, .docx)</h1>
            <input title='question doc' type='file' accept='.pdf,.doc,.docx' onChange={changeFileData} className='cursor-pointer' disabled={isExtracting} />
            
            {/* Choice modal is opened when parent triggers extraction. */}
            <Modal show={showChoiceModal} onClose={() => setShowChoiceModal(false)} popup size="sm">
                <Modal.Header />
                <Modal.Body>
                    <div className="space-y-4">
                        <h3 className="text-lg font-medium">Extract Questions</h3>
                        <p className="text-sm text-gray-600">Choose how to send the document for extraction.</p>
                        <div className="flex gap-2">
                            <Button onClick={() => { setShowChoiceModal(false); /* full text */ handleExtract(false); }} className="flex-1">Full Text</Button>
                            <Button color="light" onClick={() => { setShowChoiceModal(false); setShowPageOptions(true); }} className="flex-1">Send by Page</Button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Gemini Key entry modal (shown when backend reports no key) */}
            <Modal show={showKeyModal} onClose={() => {
                setShowKeyModal(false)
                if (keyPromiseRef.current) {
                    keyPromiseRef.current.resolve(null)
                    keyPromiseRef.current = null
                }
            }} popup size="sm">
                <Modal.Header />
                <Modal.Body>
                    <div className="space-y-4">
                        <h3 className="text-lg font-medium">Enter Gemini API Key</h3>
                        <p className="text-sm text-gray-600">We don't have a saved API key for Gemini/GPT on your account. Enter it here to use direct client extraction. The key is stored for your account only.</p>
                        <div>
                            <TextInput placeholder="Enter API key" value={geminiKeyInput} onChange={(e) => setGeminiKeyInput(e.target.value)} />
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={async () => {
                                // save and resolve
                                const key = geminiKeyInput.trim()
                                if (!key) return
                                const ok = await saveGeminiKey(key)
                                if (keyPromiseRef.current) {
                                    keyPromiseRef.current.resolve(ok ? key : null)
                                    keyPromiseRef.current = null
                                }
                                setShowKeyModal(false)
                                setGeminiKeyInput('')
                            }} disabled={savingKey || geminiKeyInput.trim() === ''}>Save Key</Button>
                            <Button color="light" onClick={() => {
                                if (keyPromiseRef.current) {
                                    keyPromiseRef.current.resolve(null)
                                    keyPromiseRef.current = null
                                }
                                setShowKeyModal(false)
                                setGeminiKeyInput('')
                            }}>Cancel</Button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            <Modal show={showPageOptions} onClose={() => setShowPageOptions(false)} popup size="md">
                <Modal.Header />
                <Modal.Body>
                    <div className="space-y-6">
                        <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                            Send PDF by Page Range
                        </h3>
                        <div>
                            <div className="mb-2 block">
                                <Label htmlFor="page-range" value="Page Range (e.g., 10-39) or 'full'" />
                            </div>
                            <TextInput id="page-range" placeholder="10-39" value={pageRange} onChange={(e) => setPageRange(e.target.value)} required />
                        </div>
                        <div>
                            <div className="mb-2 block">
                                <Label htmlFor="pages-per-file" value="Pages per File" />
                            </div>
                            <TextInput id="pages-per-file" type="number" value={pagesPerFile} onChange={(e) => setPagesPerFile(e.target.value)} required disabled={pageRange.toLowerCase() === 'full'} />
                        </div>
                        <div className="w-full flex gap-2">
                            <Button onClick={handleStartPageExtraction} disabled={isExtracting} className='flex-grow'>
                                {isExtracting ? 'Extracting...' : 'Start Extraction'}
                            </Button>
                            <Button color="light" onClick={() => { setPageRange('full'); handleStartPageExtraction(); }} disabled={isExtracting}>
                                Use Full PDF
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Extraction progress dialog (blocks interaction while extracting) */}
            <Modal show={isExtracting} onClose={() => { /* prevent closing while extracting */ }} popup size="md">
                <Modal.Header />
                <Modal.Body>
                    <div className="space-y-4">
                        <div className='flex items-center justify-center gap-3'>
                            <svg className='animate-spin h-6 w-6 text-primary' viewBox='0 0 24 24' fill='none'>
                                <circle cx='12' cy='12' r='10' stroke='currentColor' strokeOpacity='0.2' strokeWidth='4' />
                                <path d='M22 12a10 10 0 00-10-10' stroke='currentColor' strokeWidth='4' strokeLinecap='round' />
                            </svg>
                            <div className='text-sm text-center font-medium'>{progressMessage || 'Extracting with Gemini…'}</div>
                        </div>

                        {progress !== null && (
                            <div>
                                <div className='flex items-center justify-between mb-1'>
                                    <div className='text-sm font-medium text-bodydark2'>Extracting...</div>
                                    <div className='text-sm font-medium text-bodydark2'>{Math.round(progress)}%</div>
                                </div>
                                <Progress progress={progress} color="indigo" className='w-full' />
                            </div>
                        )}
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    )
}

export default ExtractQuestionsFromFile
